import { useEffect, useState } from 'react';
import { updateCatch, deleteCatch, type UpdateCatchRequest } from '../../api/catches';
import { listFishTypes } from '../../api/fishTypes';
import { listLureTypes } from '../../api/lureTypes';
import type { CatchResponse, FishTypeResponse, LureTypeResponse } from '../../types/api';
import { ConfirmDialog } from '../ConfirmDialog';
import { inputClass } from '../ui/inputClass';

interface Props {
  catchItem: CatchResponse | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: CatchResponse) => void;
  onDeleted: (id: string) => void;
}

export function EditCatchDialog({ catchItem, open, onClose, onSaved, onDeleted }: Props) {
  const [fishTypes, setFishTypes] = useState<FishTypeResponse[]>([]);
  const [fishTypeId, setFishTypeId] = useState<string>('');
  const [lureTypes, setLureTypes] = useState<LureTypeResponse[]>([]);
  const [lureTypeId, setLureTypeId] = useState<string>('');
  const [lengthInches, setLengthInches] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isPublic, setIsPublic] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open && fishTypes.length === 0) {
      listFishTypes().then(setFishTypes).catch(() => { /* ignore */ });
    }
    if (open && lureTypes.length === 0) {
      listLureTypes().then(setLureTypes).catch(() => { /* ignore */ });
    }
  }, [open, fishTypes.length, lureTypes.length]);

  useEffect(() => {
    if (catchItem) {
      setFishTypeId(String(catchItem.fishTypeId));
      setLureTypeId(catchItem.lureTypeId != null ? String(catchItem.lureTypeId) : '');
      setLengthInches(catchItem.lengthInches != null ? String(catchItem.lengthInches) : '');
      setNotes(catchItem.notes ?? '');
      setIsPublic(catchItem.isPublic);
      setError(null);
    }
  }, [catchItem]);

  if (!open || !catchItem) return null;

  async function handleSave() {
    if (!catchItem) return;
    setBusy(true);
    setError(null);
    try {
      const req: UpdateCatchRequest = {
        spotId: catchItem.spotId ?? undefined,
        fishTypeId: Number(fishTypeId),
        ...(lureTypeId ? { lureTypeId: Number(lureTypeId) } : {}),
        latitude: catchItem.latitude,
        longitude: catchItem.longitude,
        caughtAt: catchItem.caughtAt,
        ...(lengthInches ? { lengthInches: Number(lengthInches) } : {}),
        ...(notes ? { notes } : {}),
        isPublic,
      };
      const updated = await updateCatch(catchItem.id, req);
      onSaved(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!catchItem) return;
    setBusy(true);
    try {
      await deleteCatch(catchItem.id);
      onDeleted(catchItem.id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold">Edit catch</h2>
        <p className="mb-4 text-xs text-slate-500">
          {catchItem.userDisplayName} • {new Date(catchItem.caughtAt).toLocaleString()}
        </p>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Fish type</span>
          <select
            value={fishTypeId}
            onChange={(e) => setFishTypeId(e.target.value)}
            className={inputClass}
          >
            {fishTypes.map(ft => (
              <option key={ft.id} value={ft.id}>{ft.name}</option>
            ))}
          </select>
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Lure <span className="font-normal text-slate-400">— optional</span></span>
          <select
            value={lureTypeId}
            onChange={(e) => setLureTypeId(e.target.value)}
            className={inputClass}
          >
            <option value="">— None —</option>
            {lureTypes.map(lt => (
              <option key={lt.id} value={lt.id}>{lt.name}</option>
            ))}
          </select>
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Length (inches)</span>
          <input
            type="number"
            step="0.1"
            min="0"
            value={lengthInches}
            onChange={(e) => setLengthInches(e.target.value)}
            className={inputClass}
          />
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass}
            rows={3}
          />
        </label>

        <div className="mb-4 flex items-center justify-between gap-3">
          <span className="text-sm font-medium">
            {isPublic ? 'Public catch' : 'Private catch'}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={isPublic}
            onClick={() => setIsPublic((v) => !v)}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
              isPublic ? 'bg-teal-600 dark:bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isPublic ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {error && <p className="mb-2 text-sm text-red-500">{error}</p>}

        <div className="flex justify-between gap-2">
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            className="rounded-md border border-red-300 text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
          >
            Delete
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border px-4 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <ConfirmDialog
          open={confirmDelete}
          title="Delete catch?"
          description={`Delete ${catchItem.userDisplayName}'s catch of ${catchItem.fishTypeName} on ${new Date(catchItem.caughtAt).toLocaleString()}?`}
          confirmLabel="Delete"
          confirmTone="danger"
          busy={busy}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      </div>
    </div>
  );
}
